/**
 * UserMessage Component
 *
 * Renders user messages in the conversation view matching the Figma design.
 * Features right-aligned layout, grey background, and action controls.
 *
 * Features:
 * - Right-aligned layout as per Figma design
 * - Grey background (#F1F2F5) with rounded corners
 * - Message actions (copy, save, edit, delete) using MessageActions component
 * - Responsive design for mobile
 * - Accessibility compliance
 * - CSS Modules for styling
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Group,
  Textarea,
  Button,
  Tooltip,
} from '@mantine/core';
import { useHover, useClickOutside } from '@mantine/hooks';
import {
  IconX,
} from '@tabler/icons-react';
import { Icon } from '../Icon/Icon';
import {
  MessageActions,
  createCopyAction,
  createSaveAction,
  createEditAction,
  createDeleteAction,
} from '../MessageActions';
import type { MessageForFeedback } from '../../../../utils/feedbackTransform';
import { MessageMarkdown } from '../MessageMarkdown';
import classes from './UserMessage.module.css';
import type {
  MessageComponentProps,
  MessageActionType
} from '../../types/conversation';
import { useUserSettingsStore } from '../../../../stores/userSettingsStore';
import { useIsMessageSending } from '../../../../hooks';
import { showNotification } from '../../../../components/Notification';
import { FEATURES } from '../../../../config/featureFlags';
import { trackComponentInteraction } from '../../../../utils/analytics';

// =============================================================================
// COMPONENT INTERFACES
// =============================================================================

interface UserMessageState {
  isEditing: boolean;
  editValue: string;
  showMenu: boolean;
}

// =============================================================================
// STYLES
// =============================================================================

// Styles are now in UserMessage.module.css following Mantine v7 best practices

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const UserMessage: React.FC<MessageComponentProps> = ({
  message,
  showActions = true,
  onAction,
  conversationId,
  className,
  style,
}) => {
  // =============================================================================
  // STATE AND REFS
  // =============================================================================

  const { ref: hoverRef } = useHover();
  const [state, setState] = useState<UserMessageState>({
    isEditing: false,
    editValue: message.content,
    showMenu: false,
  });

  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const clickOutsideRef = useClickOutside(() => {
    if (state.isEditing) {
      handleCancelEdit();
    }
  });

  const { isMessageSaved, saveMessage } = useUserSettingsStore();

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const handleAction = (actionType: MessageActionType, payload?: string) => {
    onAction?.(actionType, message.id, payload);
  };

  // Transform message for feedback compatibility
  const messageForFeedback: MessageForFeedback = {
    id: message.id,
    content: message.content,
    role: message.role,
    metadata: {
      originalMessageId: message.id, // Use the message ID as the original ID
      ...message.metadata,
    },
  };

  const handleEdit = () => {
    setState(prev => ({
      ...prev,
      isEditing: true,
      editValue: message.content,
    }));

    // Focus input after state update
    setTimeout(() => {
      editInputRef.current?.focus();

      const length = message.content.length;
      editInputRef.current?.setSelectionRange(length, length);
    }, 0);
  };

  const handleSaveEdit = () => {
    const trimmedValue = state.editValue.trim();

    if (trimmedValue) {
      // Pass the new content to the parent component
      handleAction('edit', trimmedValue);
    }

    setState(prev => ({
      ...prev,
      isEditing: false,
    }));
  };

  const handleCancelEdit = () => {
    setState(prev => ({
      ...prev,
      isEditing: false,
      editValue: message.content,
    }));
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      trackComponentInteraction(
      'Save Edit',
      'save edit',
      'button',
      'click',
      'save_edit')
      event.preventDefault();
      handleSaveEdit();
    } else if (event.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleCopy = async () => {
      trackComponentInteraction(
      'Copy Prompt',
      'copy',
      'button',
      'click',
      'copy_prompt'
    );
      await navigator.clipboard.writeText(message.content);
      handleAction('copy');
  };

  const handleSave = async () => {
    try {
      if (isMessageSaved(message.content)) {
        showNotification('Prompt already saved. Check \'Your Saved Prompts\' in Prompt Library.');
        return;
      }

      trackComponentInteraction(
        'ChatMessage',
        'Save Prompt',
        'button',
        'click',
        'prompt_saved'
      );

      await saveMessage(message.content);
      handleAction('save');
    } catch (error) {
      console.error('Failed to save message:', error);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      handleAction('delete');
    }
  };

  const handleFileAction = (action: string, messageId: string, filename: string) => {
    // Handle file-specific actions (download, preview, etc.)
    // TODO: Implement file actions (download, preview, etc.)
    if (action === 'download') {
      // Trigger file download
    }
  };

  // =============================================================================
  // RENDER HELPERS
  // =============================================================================

  const renderContent = () => {
    if (state.isEditing) {
      return (
        <div>
          <Textarea
            ref={editInputRef}
            value={state.editValue}
            onChange={(event) => {
              const newValue = event.currentTarget.value;
              setState(prev => ({
                ...prev,
                editValue: newValue,
              }));
            }}
            onKeyDown={handleKeyPress}
            className={classes.editInput}
            classNames={{
              input: classes.editInputTextarea,
            }}
            autosize
          />

          <Group className={classes.editControls}>
            <div className={classes.editControlsContainer}>
            <Tooltip
              label={"Cancel"}
              position="top"
            >
            <Button
              classNames={{
                root: classes.editCancelButton,
                inner: classes.editCancelButtonInner,
              }}
              variant="subtle"
              onClick={handleCancelEdit}
              aria-label="Cancel edit"
            >
              <IconX />
            </Button>
            </Tooltip>
            <Button
              classNames={{
                root: classes.editUpdateButton,
                label: classes.editUpdateButtonLabel,
                section: classes.editUpdateButtonSection,
              }}
              leftSection={<Icon name="send" size={24} color="#FFFFFF" />}
              onClick={handleSaveEdit}
            >
              Update
            </Button>
            </div>
          </Group>
        </div>
      );
    }

    return (
      <MessageMarkdown
        content={message.content}
        onCitationClick={(citationId) => {
        }}
        onFollowUpClick={(question) => {
        }}
        className={classes.content}
      />
    );
  };

  const renderActions = () => {
    const isMessageSending = useIsMessageSending();

    if (!showActions || state.isEditing) return null;

    // Create actions matching Figma design exactly: Copy, Edit, Delete
    let primaryActions;
    if (!FEATURES.showSavePrompts) {
      primaryActions = [
        createCopyAction(handleCopy),
        createEditAction(handleEdit, isMessageSending),
      ];
    } else {
      primaryActions = [
        createCopyAction(handleCopy),
        createSaveAction(handleSave),
        createEditAction(handleEdit, isMessageSending),
        // TODO: this delete button is not in scope for phase 1
        // createDeleteAction(handleDelete),
      ];
    }

    return (
      <MessageActions
        primaryActions={primaryActions}
        secondaryActions={[]}
        menuActions={[]}
        showSpacer={false}
        className={classes.actions}
        message={messageForFeedback}
        conversationId={conversationId}
        // TODO: Add userName from auth store when available
      />
    );
  };


  // =============================================================================
  // MAIN RENDER
  // =============================================================================

  return (
    <Box className={`${classes.container} ${className || ''}`} style={style}>
      <div ref={clickOutsideRef}>
        <Box
          ref={hoverRef}
          className={`${classes.messageBox} ${state.isEditing ? classes.messageBoxEdit : ''}`}
          id={`message-${message.id}`}
        >
          {/* File attachments moved outside message bubble - see ConversationContent */}

          {renderContent()}
          {renderActions()}
        </Box>
      </div>
    </Box>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default UserMessage;
export type { UserMessageState };
